<template>
  <div class="wrraper products-page">
    <Breadcrumb
      :items="items"
      search_route="/products/add"
      :search_title="$t('addNew')"
      icon="fa-plus"
    />

    <product-filter />

    <div class="fadeIn">
      <!-- Page toolbar -->
      <section class="products-toolbar">
        <div class="toolbar-card">
          <div class="toolbar-card__header">
            <div class="toolbar-card__title">
              <h2 class="page-title">
                {{ $t("breadcrumb.products.title") }}
                <span class="page-title__count">{{ statisticsItem.number || 0 }}</span>
              </h2>
              <p class="page-subtitle">{{ $t("products.pageSubtitle") }}</p>
            </div>

            <div class="toolbar-card__actions">
              <v-btn
                v-if="selected.length > 0"
                class="bulk-delete-btn"
                outlined
                depressed
                @click="deleteSelected"
              >
                <v-icon small left>fal fa-trash-alt</v-icon>
                {{ $t("table.deleteSelected") }} ({{ selected.length }})
              </v-btn>
            </div>
          </div>

          <div class="stats-row">
            <div class="stat-box">
              <div class="stat-box__icon stat-box__icon--total">
                <v-icon small color="white">fal fa-boxes</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ statisticsItem.number || 0 }}</strong>
                <span>{{ $t("products.statsTotal") }}</span>
              </div>
            </div>
            <div class="stat-box">
              <div class="stat-box__icon stat-box__icon--active">
                <v-icon small color="white">fal fa-check-circle</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ activeOnPage }}</strong>
                <span>{{ $t("products.statsActive") }}</span>
              </div>
            </div>
            <div class="stat-box">
              <div class="stat-box__icon stat-box__icon--inactive">
                <v-icon small color="white">fal fa-ban</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ inactiveOnPage }}</strong>
                <span>{{ $t("products.statsInactive") }}</span>
              </div>
            </div>
            <div class="stat-box" :class="{ 'stat-box--highlight': selected.length > 0 }">
              <div class="stat-box__icon stat-box__icon--selected">
                <v-icon small color="white">fal fa-check-square</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ selected.length }}</strong>
                <span>{{ $t("products.statsSelected") }}</span>
              </div>
            </div>
          </div>

          <div class="tools-panel">
            <div class="tools-panel__head">
              <v-icon x-small class="mr-1">fal fa-tools</v-icon>
              <span>{{ $t("products.toolsTitle") }}</span>
            </div>
            <p v-if="hasActiveFilters" class="tools-panel__hint">
              <v-icon x-small class="tools-panel__hint-icon">fal fa-filter</v-icon>
              {{ $t("products.toolsFilteredHint", { count: statisticsItem.number || 0 }) }}
            </p>

            <div class="tools-grid">
              <div class="tools-group">
                <span class="tools-group__label">{{ $t("products.groupCatalog") }}</span>
                <div class="tools-group__actions">
                  <button
                    class="tool-btn"
                    type="button"
                    :disabled="loadingXml"
                    @click="generateAndDownloadXML"
                  >
                    <v-progress-circular
                      v-if="loadingXml"
                      indeterminate
                      color="primary"
                      size="16"
                      width="2"
                    />
                    <template v-else>
                      <v-icon x-small>fal fa-file-code</v-icon>
                      {{ $t("products.downloadXml") }}
                    </template>
                  </button>
                  <button
                    class="tool-btn"
                    type="button"
                    :disabled="!!merchantUrlsLoading"
                    @click="downloadMerchantUrls('ar')"
                  >
                    <v-progress-circular
                      v-if="merchantUrlsLoading === 'ar'"
                      indeterminate
                      color="primary"
                      size="16"
                      width="2"
                    />
                    <template v-else>
                      <v-icon x-small>fal fa-link</v-icon>
                      {{ $t("products.downloadMerchantUrlsAr") }}
                    </template>
                  </button>
                  <button
                    class="tool-btn"
                    type="button"
                    :disabled="!!merchantUrlsLoading"
                    @click="downloadMerchantUrls('en')"
                  >
                    <v-progress-circular
                      v-if="merchantUrlsLoading === 'en'"
                      indeterminate
                      color="primary"
                      size="16"
                      width="2"
                    />
                    <template v-else>
                      <v-icon x-small>fal fa-link</v-icon>
                      {{ $t("products.downloadMerchantUrlsEn") }}
                    </template>
                  </button>
                  <button
                    class="tool-btn tool-btn--danger"
                    type="button"
                    :disabled="clearOffersLoading"
                    @click="dialogClearOffers = true"
                  >
                    <v-icon x-small>fal fa-times-circle</v-icon>
                    {{ $t("products.clearOffers") }}
                  </button>
                </div>
              </div>

              <div class="tools-group">
                <span class="tools-group__label">{{ $t("products.groupSheet") }}</span>
                <div class="tools-group__actions">
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="downloadImportSample('products-export', 'product_sample.xlsx')"
                  >
                    <v-icon x-small>fal fa-download</v-icon>
                    {{ $t("products.downloadSheet") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModel = true">
                    <v-icon x-small>fal fa-upload</v-icon>
                    {{ $t("products.importSheet") }}
                  </button>
                  <button
                    class="tool-btn"
                    type="button"
                    :disabled="exportSheetLoading"
                    @click="exportProductsSheet"
                  >
                    <v-progress-circular
                      v-if="exportSheetLoading"
                      indeterminate
                      color="primary"
                      size="16"
                      width="2"
                    />
                    <template v-else>
                      <v-icon x-small>fal fa-file-export</v-icon>
                      {{ $t("products.exportSheet") }}
                    </template>
                  </button>
                </div>
              </div>

              <div class="tools-group">
                <span class="tools-group__label">{{ $t("products.groupPricing") }}</span>
                <div class="tools-group__actions">
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="downloadImportSample('products-price-export', 'product_price_sample.xlsx')"
                  >
                    <v-icon x-small>fal fa-tags</v-icon>
                    {{ $t("products.downloadCodePrice") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModelCodePrice = true">
                    <v-icon x-small>fal fa-upload</v-icon>
                    {{ $t("products.importCodePrice") }}
                  </button>
                  <button
                    class="tool-btn"
                    type="button"
                    :disabled="exportCodePriceLoading"
                    @click="exportProductsCodePrice"
                  >
                    <v-progress-circular
                      v-if="exportCodePriceLoading"
                      indeterminate
                      color="primary"
                      size="16"
                      width="2"
                    />
                    <template v-else>
                      <v-icon x-small>fal fa-file-export</v-icon>
                      {{ $t("products.exportCodePrice") }}
                    </template>
                  </button>
                </div>
              </div>

              <div class="tools-group">
                <span class="tools-group__label">{{ $t("products.groupStock") }}</span>
                <div class="tools-group__actions">
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="
                      downloadImportSample(
                        'products/stock-import-sample',
                        'stock_import_sample.xlsx'
                      )
                    "
                  >
                    <v-icon x-small>fal fa-download</v-icon>
                    {{ $t("products.downloadStockSample") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModelStock = true">
                    <v-icon x-small>fal fa-warehouse</v-icon>
                    {{ $t("products.importStock") }}
                  </button>
                </div>
              </div>

              <div class="tools-group">
                <span class="tools-group__label">{{ $t("products.groupAdvanced") }}</span>
                <div class="tools-group__actions">
                  <button class="tool-btn" type="button" @click="importModelImages = true">
                    <v-icon x-small>fal fa-images</v-icon>
                    {{ $t("products.importImages") }}
                  </button>
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="downloadImportSample('products/import_desc', 'product_desc_sample.xlsx')"
                  >
                    <v-icon x-small>fal fa-download</v-icon>
                    {{ $t("products.downloadDescSample") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModelProductDesc = true">
                    <v-icon x-small>fal fa-align-left</v-icon>
                    {{ $t("products.importDescription") }}
                  </button>
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="downloadImportSample('products/import_seo', 'product_seo_sample.xlsx')"
                  >
                    <v-icon x-small>fal fa-download</v-icon>
                    {{ $t("products.downloadSeoSample") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModelProductSeo = true">
                    <v-icon x-small>fal fa-search</v-icon>
                    {{ $t("products.importSeo") }}
                  </button>
                  <button
                    type="button"
                    class="tool-btn tool-btn--link"
                    @click="
                      downloadImportSample(
                        'products-import-price-offer-sample',
                        'product_price_offer_sample.xlsx'
                      )
                    "
                  >
                    <v-icon x-small>fal fa-download</v-icon>
                    {{ $t("products.downloadPriceOfferSample") }}
                  </button>
                  <button class="tool-btn" type="button" @click="importModelPriceOffer = true">
                    <v-icon x-small>fal fa-percent</v-icon>
                    {{ $t("products.importPriceOffer") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main class="products-table-wrap">
        <v-data-table
          class="elevation-0 products-table thumb diff_table"
          :headers="headers"
          :items="rows"
          :search="search"
          :loading="loading"
          :loading-text="$t('table.loadingData')"
          item-key="id"
          :items-per-page="paginations.items_per_page"
          :show-select="true"
          v-model="selected"
          hide-default-footer
        >
          <template v-slot:no-data>
            <div class="table-empty">
              <v-icon size="40" color="grey lighten-1">fal fa-box-open</v-icon>
              <p>{{ $t("table.noData") }}</p>
            </div>
          </template>

          <template v-slot:[`item.index`]="{ index }">
            {{ index + 1 }}
          </template>

          <template v-slot:[`item.media`]="{ item }">
            <div class="product-thumb" @click="showProductImage(item)">
              <img
                v-if="item.media.length"
                :src="item.media[0].image"
                :alt="item.name"
                class="product-thumb__img"
              />
              <img
                v-else-if="item.product_details"
                :src="item.product_details[0].media[0].image"
                :alt="item.name"
                class="product-thumb__img"
              />
              <div v-else class="product-thumb__placeholder">
                <v-icon small>fal fa-image</v-icon>
              </div>
            </div>
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <span class="product-name" :title="item.name">{{ item.name }}</span>
          </template>

          <template v-slot:[`item.code`]="{ item }">
            <span class="product-code">{{ item.code || "—" }}</span>
          </template>

          <template v-slot:[`item.desc`]="{ item }">
            <button
              v-if="item.desc"
              type="button"
              class="desc-btn"
              :title="$t('products.viewDescription')"
              @click="showDesc(item)"
            >
              <v-icon x-small>fal fa-eye</v-icon>
            </button>
            <span v-else class="text-muted">—</span>
          </template>

          <template v-slot:[`item.is_active`]="{ item }">
            <span
              class="status"
              :class="item.is_active ? 'success' : 'canceled'"
            >
              {{ item.is_active ? $t("active") : $t("inactive") }}
            </span>
          </template>

          <template v-slot:[`item.report_reason`]="{ item }">
            <button
              v-if="item.report_reason"
              type="button"
              class="desc-btn"
            >
              <v-icon x-small>fal fa-eye</v-icon>
            </button>
            <span v-else class="text-muted">—</span>
          </template>

          <template v-slot:[`item.is_most_order`]="{ item }">
            <button
              v-if="!item.is_most_order"
              type="button"
              class="most-order-btn"
              @click="mostOrder(item)"
            >
              <v-icon x-small>fal fa-link</v-icon>
              {{ $t("products.addToMostOrdered") }}
            </button>
            <span v-else class="most-order-badge">
              <v-icon x-small>fal fa-check</v-icon>
              {{ $t("products.addedToMostOrdered") }}
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="row-actions">
              <button
                type="button"
                class="row-action row-action--view"
                :title="$t('show')"
                @click="showItem(item)"
              >
                <v-icon x-small>fal fa-eye</v-icon>
              </button>
              <button
                type="button"
                class="row-action row-action--edit"
                :title="$t('edit')"
                @click="editItem(item)"
              >
                <v-icon x-small>fal fa-edit</v-icon>
              </button>
              <button
                type="button"
                class="row-action row-action--delete"
                :title="$t('delete')"
                @click="deleteItem(item)"
              >
                <v-icon x-small>fal fa-trash</v-icon>
              </button>
            </div>
          </template>
        </v-data-table>
      </main>

      <!-- Pagination -->
      <div class="products-pagination">
        <v-pagination
          color="primary"
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="7"
          @input="fetchData($event)"
        ></v-pagination>
      </div>
    </div>

    <!-- Confirm dialogs -->
    <v-dialog v-model="dialogDelete" max-width="440" content-class="products-dialog">
      <v-card class="pp-dialog pp-dialog--danger">
        <div class="pp-dialog__hero">
          <div class="pp-dialog__icon pp-dialog__icon--warning">
            <v-icon color="white">fal fa-exclamation-triangle</v-icon>
          </div>
          <h3 class="pp-dialog__title">{{ $t("table.deletedialog.areYouSure") }}</h3>
        </div>
        <div class="pp-dialog__footer">
          <button type="button" class="dialog-btn dialog-btn--cancel" @click="closeDeleteDialog">
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button type="button" class="dialog-btn dialog-btn--danger" @click="deleteItemConfirm">
            <v-icon x-small>fal fa-trash-alt</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete_selected" max-width="440" content-class="products-dialog">
      <v-card class="pp-dialog pp-dialog--danger">
        <div class="pp-dialog__hero">
          <div class="pp-dialog__icon pp-dialog__icon--warning">
            <v-icon color="white">fal fa-exclamation-triangle</v-icon>
          </div>
          <h3 class="pp-dialog__title">{{ $t("table.deletedialog.areYouSure") }}</h3>
        </div>
        <div class="pp-dialog__footer">
          <button type="button" class="dialog-btn dialog-btn--cancel" @click="closeDeleteSelectedDialog">
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button type="button" class="dialog-btn dialog-btn--danger" @click="deleteSelectedConfirm">
            <v-icon x-small>fal fa-trash-alt</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogClearOffers" max-width="440" content-class="products-dialog">
      <v-card class="pp-dialog pp-dialog--danger">
        <div class="pp-dialog__hero">
          <div class="pp-dialog__icon pp-dialog__icon--warning">
            <v-icon color="white">fal fa-percent</v-icon>
          </div>
          <h3 class="pp-dialog__title">{{ clearOffersConfirmText }}</h3>
        </div>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="clearOffersLoading"
            @click="closeClearOffersDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--danger"
            :disabled="clearOffersLoading"
            @click="clearOffersConfirm"
          >
            <span class="dialog-btn__spinner" v-if="clearOffersLoading"></span>
            <v-icon x-small v-else>fal fa-check</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Image preview -->
    <base-model
      @closeModel="model_1.show_model = false"
      :show="model_1.show_model"
    >
      <div class="image">
        <img
          v-if="model_1.model_img_src"
          :src="model_1.model_img_src"
          alt="..."
        />
      </div>
    </base-model>
    <base-model
      @closeModel="model_2.show_model = false"
      :show="model_2.show_model"
    >
      <div class="custom_card diff desc-modal">
        <h4>{{ $t("titles.productDesc") }}</h4>
        <div class="desc-modal__body" v-html="model_2.desc"></div>
      </div>
    </base-model>
    <v-dialog v-model="importModel" max-width="480" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importSheet") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importLoading"
            :aria-label="$t('cancel')"
            @click="closeImportSheetDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <p class="import-dialog__hint">{{ $t("products.importSheetHint") }}</p>
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="downloadImportSample('products-export', 'product_sample.xlsx')"
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadImportSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_products"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_products" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_products')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importLoading"
            @click="closeImportSheetDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importLoading || !selectedFile.file"
            @click="importFileConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="importModelCodePrice" max-width="480" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importCodePrice") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importCodePriceLoading"
            :aria-label="$t('cancel')"
            @click="closeImportCodePriceDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="downloadImportSample('products-price-export', 'product_price_sample.xlsx')"
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadImportSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_code_price"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_code_price" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_code_price')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importCodePriceLoading"
            @click="closeImportCodePriceDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importCodePriceLoading || !selectedFile.file"
            @click="importFileCodePirceConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importCodePriceLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="importModelProductDesc" max-width="480" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importDescription") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importModelProductDescLoading"
            :aria-label="$t('cancel')"
            @click="closeImportProductDescDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="downloadImportSample('products/import_desc', 'product_desc_sample.xlsx')"
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadImportSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_desc"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_desc" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_desc')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importModelProductDescLoading"
            @click="closeImportProductDescDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importModelProductDescLoading || !selectedFile.file"
            @click="importFileProductDescConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importModelProductDescLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="importModelProductSeo" max-width="480" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importSeo") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importModelProductSeoLoading"
            :aria-label="$t('cancel')"
            @click="closeImportProductSeoDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <p class="import-upload__subtitle mb-3">{{ $t("products.importSeoHint") }}</p>
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="downloadImportSample('products/import_seo', 'product_seo_sample.xlsx')"
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadImportSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_seo"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_seo" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_seo')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importModelProductSeoLoading"
            @click="closeImportProductSeoDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importModelProductSeoLoading || !selectedFile.file"
            @click="importFileProductSeoConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importModelProductSeoLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="importModelPriceOffer" max-width="480" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importPriceOffer") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importModelPriceOfferLoading"
            :aria-label="$t('cancel')"
            @click="closeImportPriceOfferDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="
              downloadImportSample(
                'products-import-price-offer-sample',
                'product_price_offer_sample.xlsx'
              )
            "
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadImportSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_offer"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_offer" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_offer')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importModelPriceOfferLoading"
            @click="closeImportPriceOfferDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importModelPriceOfferLoading || !selectedFile.file"
            @click="importFilePriceOfferConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importModelPriceOfferLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importModelStock" max-width="520" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importStock") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importStockLoading"
            :aria-label="$t('cancel')"
            @click="closeImportStockDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <p class="import-dialog__hint">{{ $t("products.importStockHint") }}</p>
          <button
            type="button"
            class="import-sample-btn"
            :disabled="importSampleLoading"
            @click="
              downloadImportSample(
                'products/stock-import-sample',
                'stock_import_sample.xlsx'
              )
            "
          >
            <v-progress-circular
              v-if="importSampleLoading"
              indeterminate
              color="primary"
              size="14"
              width="2"
            />
            <template v-else>
              <v-icon x-small>fal fa-file-download</v-icon>
              {{ $t("products.downloadStockSample") }}
            </template>
          </button>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': !!selectedFile.file }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_stock"
              accept=".xlsx,.xls,.csv"
              @change="handelSelectedFile"
            />
            <label for="file_input_stock" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{ selectedFile.file ? "fal fa-file-excel" : "fal fa-cloud-upload-alt" }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{ selectedFile.file ? selectedFile.name : $t("products.importChooseFile") }}
              </span>
              <span class="import-upload__subtitle">
                {{
                  selectedFile.file
                    ? formatFileSize(selectedFile.file.size)
                    : $t("products.importFileHint")
                }}
              </span>
            </label>
            <button
              v-if="selectedFile.file"
              type="button"
              class="import-upload__remove"
              @click="removeImportFile('file_input_stock')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>
          <div
            v-if="stockImportErrors.length"
            class="import-dialog__hint"
            style="margin-top: 12px; color: #b45309; max-height: 140px; overflow: auto"
          >
            <div v-for="(err, idx) in stockImportErrors.slice(0, 15)" :key="idx">
              Row {{ err.row }}: {{ err.message }}
            </div>
            <div v-if="stockImportErrors.length > 15">
              … +{{ stockImportErrors.length - 15 }} more
            </div>
          </div>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importStockLoading"
            @click="closeImportStockDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importStockLoading || !selectedFile.file"
            @click="importStockConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importStockLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importModelImages" max-width="520" content-class="products-dialog">
      <v-card class="pp-dialog">
        <div class="pp-dialog__head">
          <h3 class="pp-dialog__head-title">{{ $t("products.importImages") }}</h3>
          <button
            type="button"
            class="pp-dialog__close"
            :disabled="importImagesLoading"
            :aria-label="$t('cancel')"
            @click="closeImportImagesDialog"
          >
            <v-icon small>fal fa-times</v-icon>
          </button>
        </div>
        <v-card-text class="pp-dialog__body">
          <p class="import-dialog__hint">{{ $t("products.importImagesHint") }}</p>
          <div
            class="import-upload"
            :class="{ 'import-upload--filled': selectedImageFiles.length > 0 }"
          >
            <input
              type="file"
              class="import-upload__input"
              id="file_input_images"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              @change="handleSelectedImages"
            />
            <label for="file_input_images" class="import-upload__zone">
              <span class="import-upload__icon-wrap">
                <v-icon small>
                  {{
                    selectedImageFiles.length
                      ? "fal fa-images"
                      : "fal fa-cloud-upload-alt"
                  }}
                </v-icon>
              </span>
              <span class="import-upload__title">
                {{
                  selectedImageFiles.length
                    ? $t("products.importImagesSelected", {
                        count: selectedImageFiles.length,
                      })
                    : $t("products.importImagesChoose")
                }}
              </span>
              <span class="import-upload__subtitle">
                {{ $t("products.importImagesFileHint") }}
              </span>
            </label>
            <button
              v-if="selectedImageFiles.length"
              type="button"
              class="import-upload__remove"
              @click="resetSelectedImages('file_input_images')"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("products.importRemoveFile") }}
            </button>
          </div>

          <ul v-if="selectedImageFiles.length" class="import-images-list">
            <li
              v-for="(item, index) in selectedImageFiles"
              :key="`${item.code}-${index}`"
              class="import-images-list__item"
            >
              <span class="import-images-list__preview">
                <img :src="item.preview" :alt="item.name" />
              </span>
              <span class="import-images-list__meta">
                <strong class="import-images-list__code">{{ item.code }}</strong>
                <span class="import-images-list__name">{{ item.name }}</span>
              </span>
              <button
                type="button"
                class="import-images-list__remove"
                @click="removeSelectedImage(index, 'file_input_images')"
              >
                <v-icon x-small>fal fa-times</v-icon>
              </button>
            </li>
          </ul>
        </v-card-text>
        <div class="pp-dialog__footer">
          <button
            type="button"
            class="dialog-btn dialog-btn--cancel"
            :disabled="importImagesLoading"
            @click="closeImportImagesDialog"
          >
            <v-icon x-small>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="dialog-btn dialog-btn--confirm"
            :disabled="importImagesLoading || !selectedImageFiles.length"
            @click="importImagesConfirm"
          >
            <span class="dialog-btn__spinner" v-if="importImagesLoading"></span>
            <v-icon x-small v-else>fal fa-upload</v-icon>
            {{ $t("table.deletedialog.ok") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ProductFilter from "../../../components/Filters/ProductFilter.vue";

const PRODUCT_FILTER_QUERY_KEYS = [
  "keyword",
  "name_ar",
  "name_en",
  "desc_ar",
  "desc_en",
  "has_name_ar",
  "has_name_en",
  "has_desc_ar",
  "has_desc_en",
  "code",
  "is_active",
  "is_most_order",
  "category_id",
  "brand_id",
  "from_price",
  "to_price",
  "ordering",
  "prescription_requirement",
  "has_image",
  "has_offer",
  "has_price",
];
export default {
  components: { ProductFilter },
  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.products.title"),
          disabled: false,
          href: "/products/show-all",
        },
        {
          text: this.$t("breadcrumb.products.all"),
          disabled: true,
          href: "",
        },
      ],

      // ========== Statistics
      statisticsItem: {},

      // ========== Top Section
      search: "",
      xmlContent: null,

      // ========== dialog Status
      dialogDelete: false,
      loadingXml: false,
      merchantUrlsLoading: null,
      exportSheetLoading: false,
      exportCodePriceLoading: false,
      dialogDelete_selected: false,
      dialogClearOffers: false,
      clearOffersLoading: false,
      itemtoDelete: null,

      // ========== Model
      model_1: {
        show_model: false,
        model_img_src: "",
      },
      model_2: {
        show_model: false,
        desc: "",
      },

      // ========== Your Data
      rows: [], // injected in created

      // ========== elected Data
      selected: [],
      singleSelect: false,

      // ========== pagination
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      selectedFile: {
        file: null,
        name: null,
        path: null,
      },
      showItems: [
        {
          id: 1,
          value: 5,
        },
        {
          id: 2,
          value: 10,
        },
        {
          id: 1,
          value: 20,
        },
      ],
      downloadLink: null,
      // ========== Loding
      loading: false,
      importModel: false,
      importLoading: false,
      importModelCodePrice: false,
      importCodePriceLoading: false,
      importModelProductDesc: false,
      importModelProductDescLoading: false,
      importModelProductSeo: false,
      importModelProductSeoLoading: false,
      importModelPriceOffer: false,
      importModelPriceOfferLoading: false,
      importModelStock: false,
      importStockLoading: false,
      stockImportErrors: [],
      importModelImages: false,
      importImagesLoading: false,
      importSampleLoading: false,
      selectedImageFiles: [],
      request: null,
      uploadPercentage: 0,
      uploadFinshed: false,
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    activeOnPage() {
      return this.rows.filter((row) => row.is_active).length;
    },

    inactiveOnPage() {
      return this.rows.filter((row) => !row.is_active).length;
    },

    hasActiveFilters() {
      const q = this.$route.query;
      return PRODUCT_FILTER_QUERY_KEYS.some((key) => {
        const value = q[key];
        return value !== undefined && value !== null && value !== "";
      });
    },

    clearOffersConfirmText() {
      if (this.hasActiveFilters) {
        return this.$t("products.clearOffersConfirmFiltered", {
          count: this.statisticsItem.number || 0,
        });
      }
      return this.$t("products.clearOffersConfirm");
    },

    headers() {
      if (this.lang == "ar") {
        return [
          {
            text: "#",
            align: "center",
            value: "id",
            sortable: true,
          },
          {
            text: "صورة المنتج",
            align: "center",
            value: "media",
            sortable: true,
          },
          {
            text: "الاسم",
            align: "center",
            value: "name",
            sortable: true,
          },

          {
            text: "ترتيب العرض",
            align: "center",
            value: "ordering",
            sortable: true,
          },
          {
            text: "الوصف",
            align: "center",
            value: "desc",
            sortable: true,
          },
          {
            text: "الكود",
            align: "center",
            value: "code",
            sortable: true,
          },
          {
            text: "الحالة",
            align: "center",
            value: "is_active",
            sortable: true,
          },
          {
            text: "اضافة الى الاكثر طلبا",
            align: "center",
            value: "is_most_order",
            sortable: true,
          },
          {
            text: "الاجراءات",
            align: "center",
            value: "actions",
            sortable: true,
          },
        ];
      } else {
        return [
          {
            text: "#",
            align: "center",
            value: "id",
            sortable: true,
          },
          {
            text: "Product Image",
            align: "center",
            value: "media",
            sortable: true,
          },
          {
            text: "Name",
            align: "center",
            value: "name",
            sortable: true,
          },

          {
            text: "Ordering",
            align: "center",
            value: "ordering",
            sortable: true,
          },
          {
            text: "Description",
            align: "center",
            value: "desc",
            sortable: true,
          },
          {
            text: "Code",
            align: "center",
            value: "code",
            sortable: true,
          },
          {
            text: "Status",
            align: "center",
            value: "is_active",
            sortable: true,
          },
          {
            text: "Add To Most Order",
            align: "center",
            value: "is_most_order",
            sortable: true,
          },
          {
            text: "Actions",
            align: "center",
            value: "actions",
            sortable: true,
          },
        ];
      }
    },
  },

  methods: {
    appendQueryParam(params, key, value) {
      if (value === undefined || value === null || value === "") return;
      params.append(key, value);
    },

    buildFilterParams() {
      const q = this.$route.query;
      const params = new URLSearchParams();

      if (q.keyword) params.append("keyword", q.keyword);
      if (q.name_ar) params.append("name_ar", q.name_ar);
      if (q.name_en) params.append("name_en", q.name_en);
      if (q.desc_ar) params.append("desc_ar", q.desc_ar);
      if (q.desc_en) params.append("desc_en", q.desc_en);
      this.appendQueryParam(params, "has_name_ar", q.has_name_ar);
      this.appendQueryParam(params, "has_name_en", q.has_name_en);
      this.appendQueryParam(params, "has_desc_ar", q.has_desc_ar);
      this.appendQueryParam(params, "has_desc_en", q.has_desc_en);
      if (q.code) params.append("code", q.code);
      this.appendQueryParam(params, "is_active", q.is_active);
      this.appendQueryParam(params, "is_most_order", q.is_most_order);
      if (q.category_id) params.append("category_id", q.category_id);
      if (q.brand_id) params.append("brand_id", q.brand_id);
      if (q.from_price) params.append("from_price", q.from_price);
      if (q.to_price) params.append("to_price", q.to_price);
      if (q.ordering) params.append("ordering", q.ordering);
      this.appendQueryParam(params, "prescription_requirement", q.prescription_requirement);
      this.appendQueryParam(params, "has_image", q.has_image);
      this.appendQueryParam(params, "has_offer", q.has_offer);
      this.appendQueryParam(params, "has_price", q.has_price);

      return params;
    },

    filterParamsObject() {
      const params = {};
      this.buildFilterParams().forEach((value, key) => {
        params[key] = value;
      });
      return params;
    },

    async downloadExcelExport(url, filename, loadingKey, withFilters = true) {
      this[loadingKey] = true;
      try {
        const response = await this.$axios.get(url, {
          params: withFilters ? this.filterParamsObject() : { _t: Date.now() },
          responseType: "blob",
        });

        const contentType = response.headers["content-type"] || "";
        if (contentType.includes("application/json")) {
          const text = await response.data.text();
          const payload = JSON.parse(text);
          throw new Error(payload.message || this.$t("products.exportFailed"));
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (err) {
        let message = this.$t("products.exportFailed");
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const payload = JSON.parse(text);
            message = payload.message || message;
          } catch (parseError) {
            // keep default message
          }
        } else if (err.response?.data?.message) {
          message = err.response.data.message;
        } else if (err.message) {
          message = err.message;
        }

        this.$iziToast.error({
          timeout: 2500,
          message,
          position: "bottomRight",
        });
      } finally {
        this[loadingKey] = false;
      }
    },

    exportProductsSheet() {
      const stamp = new Date().toISOString().slice(0, 10);
      this.downloadExcelExport(
        "products/export-sheet",
        `products_${stamp}.xlsx`,
        "exportSheetLoading"
      );
    },

    exportProductsCodePrice() {
      const stamp = new Date().toISOString().slice(0, 10);
      this.downloadExcelExport(
        "products/export-code-price",
        `products-code-price_${stamp}.xlsx`,
        "exportCodePriceLoading"
      );
    },

    async downloadMerchantUrls(locale) {
      this.merchantUrlsLoading = locale;
      try {
        const stamp = new Date().toISOString().slice(0, 10);
        const response = await this.$axios.get("products/merchant-urls", {
          params: {
            ...this.filterParamsObject(),
            locale,
          },
          responseType: "blob",
        });

        const contentType = response.headers["content-type"] || "";
        if (contentType.includes("application/json")) {
          const text = await response.data.text();
          const payload = JSON.parse(text);
          throw new Error(payload.message || this.$t("products.exportFailed"));
        }

        const blob = new Blob([response.data], {
          type: "text/plain;charset=utf-8",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `google-merchant-product-urls-${locale}_${stamp}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (err) {
        let message = this.$t("products.exportFailed");
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const payload = JSON.parse(text);
            message = payload.message || message;
          } catch (parseError) {
            // keep default message
          }
        } else if (err.response?.data?.message) {
          message = err.response.data.message;
        } else if (err.message) {
          message = err.message;
        }

        this.$iziToast.error({
          timeout: 2500,
          message,
          position: "bottomRight",
        });
      } finally {
        this.merchantUrlsLoading = null;
      }
    },

    downloadImportSample(url, filename) {
      this.downloadExcelExport(url, filename, "importSampleLoading", false);
    },

    importFileConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      } else {
        this.importLoading = true;
        const frmData = new FormData();
        frmData.append("products_file", this.selectedFile.file);
        this.$axios
          .post("products-import", frmData)
          .then((res) => {
            this.importModel = false;
            this.importLoading = false;
            this.$iziToast.success({
              timeout: 3000,
              message: res.data?.message || this.$t("products.importSheetSuccess"),
              position: "bottomRight",
            });
            this.resetSelectedFile("file_input_products");
            this.setRows();
          })
          .catch((err) => {
            // this.importModel = false;
            this.importLoading = false;
            this.$iziToast.error({
              timeout: 2000,
              message:
                err.response?.data?.message ||
                this.$t("validation.error"),
              position: "bottomRight",
            });
          });
      }
    },
    importFileCodePirceConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      } else {
        this.importCodePriceLoading = true;
        const frmData = new FormData();
        frmData.append("product_price_file", this.selectedFile.file);
        this.$axios
          .post("products-price-import", frmData)
          .then(() => {
            this.importModelCodePrice = false;
            this.importCodePriceLoading = false;
            this.$iziToast.success({
              timeout: 2000,
              message: "تم التحميل بنجاح",
              position: "bottomRight",
            });
            this.resetSelectedFile("file_input_code_price");
            this.setRows();
          })
          .catch((err) => {
            // this.importModel = false;
            this.importCodePriceLoading = false;
            this.$iziToast.error({
              timeout: 2000,
              message: err.response.data.message,
              position: "bottomRight",
            });
          });
      }
    },
    importFileProductDescConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      } else {
        this.importModelProductDescLoading = true;
        const frmData = new FormData();
        frmData.append("products_file", this.selectedFile.file);
        this.$axios
          .post("import_products_desc", frmData)
          .then(() => {
            this.importModelProductDesc = false;
            this.importModelProductDescLoading = false;
            this.$iziToast.success({
              timeout: 2000,
              message: "تم التحميل بنجاح",
              position: "bottomRight",
            });
            this.resetSelectedFile("file_input_desc");
            this.setRows();
          })
          .catch((err) => {
            this.importModelProductDescLoading = false;
            this.$iziToast.error({
              timeout: 2000,
              message: err.response.data.message,
              position: "bottomRight",
            });
          });
      }
    },
    importFileProductSeoConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      }
      this.importModelProductSeoLoading = true;
      const frmData = new FormData();
      frmData.append("products_file", this.selectedFile.file);
      this.$axios
        .post("import_products_seo", frmData)
        .then((res) => {
          this.importModelProductSeo = false;
          this.importModelProductSeoLoading = false;
          const updated = res?.data?.data?.updated;
          const message =
            typeof updated === "number"
              ? this.$t("products.importSeoSuccess", { count: updated })
              : "تم التحميل بنجاح";
          this.$iziToast.success({
            timeout: 2000,
            message,
            position: "bottomRight",
          });
          this.resetSelectedFile("file_input_seo");
          this.setRows();
        })
        .catch((err) => {
          this.importModelProductSeoLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "فشل الاستيراد",
            position: "bottomRight",
          });
        });
    },
    importFilePriceOfferConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      } else {
        this.importModelPriceOfferLoading = true;
        const frmData = new FormData();
        frmData.append("product_price_file", this.selectedFile.file);
        this.$axios
          .post("ImportFileProductPriceOffer", frmData)
          .then(() => {
            this.importModelPriceOffer = false;
            this.importModelPriceOfferLoading = false;
            this.$iziToast.success({
              timeout: 2000,
              message: "تم التحميل بنجاح",
              position: "bottomRight",
            });
            this.resetSelectedFile("file_input_offer");
            this.setRows();
          })
          .catch((err) => {
            this.importModelPriceOfferLoading = false;
            this.$iziToast.error({
              timeout: 2000,
              message: err.response.data.message,
              position: "bottomRight",
            });
          });
      }
    },
    importStockConfirm() {
      if (!this.selectedFile.file) {
        this.$iziToast.error({
          timeout: 2000,
          message: "يرجي اختيار الملف اولا ",
          position: "bottomRight",
        });
        return;
      }
      this.importStockLoading = true;
      this.stockImportErrors = [];
      const frmData = new FormData();
      frmData.append("stock_file", this.selectedFile.file);
      this.$axios
        .post("products/stock-import", frmData)
        .then((res) => {
          this.importStockLoading = false;
          const data = res?.data?.data || {};
          this.stockImportErrors = Array.isArray(data.errors) ? data.errors : [];
          this.$iziToast.success({
            timeout: 3500,
            message:
              res?.data?.message ||
              this.$t("products.importStockSuccess", {
                branches: data.upserted_branch_rows || 0,
                products: data.updated_products || 0,
                errors: this.stockImportErrors.length,
              }),
            position: "bottomRight",
          });
          if (!this.stockImportErrors.length) {
            this.importModelStock = false;
            this.resetSelectedFile("file_input_stock");
          }
          this.setRows();
        })
        .catch((err) => {
          this.importStockLoading = false;
          const data = err.response?.data?.data || {};
          this.stockImportErrors = Array.isArray(data.errors) ? data.errors : [];
          this.$iziToast.error({
            timeout: 3500,
            message:
              err.response?.data?.message || this.$t("products.importStockFailed"),
            position: "bottomRight",
          });
        });
    },
    // ===== Search Method =====
    filterClick(word) {
      if (!this.loading) {
        this.search = word;
        this.helper_filterSearch();
      }
    },

    // img Model
    showProductImage(item) {
      const src =
        item.media?.length
          ? item.media[0].image
          : item.product_details?.[0]?.media?.[0]?.image;
      if (src) {
        this.model_1.model_img_src = src;
        this.model_1.show_model = true;
      }
    },
    show_model_1(e) {
      this.model_1.model_img_src = e.target.src;
      this.model_1.show_model = true;
    },
    showDesc(e) {
      console.log(e);
      this.model_2.desc = e.desc;
      this.model_2.show_model = true;
    },

    // ==================== Start CRUD ====================
    addItem() {
      this.$router.push({ path: "/products/add" });
    },
    showItem(item) {
      this.$router.push({ path: "/products/show/" + item.id });
    },
    editItem(item) {
      this.$router.push({ path: "/products/edit/" + item.id });
    },

    // ===== Delete
    deleteItem(item) {
      this.dialogDelete = true;
      this.itemtoDelete = item;
    },
    deleteItemConfirm() {
      this.$axios({
        method: "DELETE",
        url: `products/${this.itemtoDelete.id}`,
      })
        .then(() => {
          this.rows = this.rows.filter((item) => {
            return item.id != this.itemtoDelete.id;
          });
          this.dialogDelete = false;
          this.$iziToast.success({
            timeout: 2000,
            message: this.$t("deleteSuccess"),
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.dialogDelete = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response.data.message,
            position: "bottomRight",
          });
        });
    },
    deleteSelected() {
      this.dialogDelete_selected = true;
    },
    deleteSelectedConfirm() {
      let submit_data = new FormData();
      this.selected.forEach((item, index) => {
        submit_data.append(`productIds[${index}]`, item.id);
      });
      this.$axios({
        method: "POST",
        url: `delete_multi_products`,
        // data: { ids: this.selected.map((item) => item.id) },
        data: submit_data,
      })
        .then(() => {
          this.rows = this.rows.filter((item) => {
            return !this.selected.filter((obj) => obj.id == item.id).length > 0;
          });
          this.dialogDelete_selected = false;
          this.$iziToast.error({
            timeout: 2000,
            message: this.$t("deleteSuccess"),
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.dialogDelete_selected = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response.data.message,
            position: "bottomRight",
          });
        });
    },
    clearOffersConfirm() {
      this.clearOffersLoading = true;
      this.$axios({
        method: "POST",
        url: "products/clear-offers",
        params: this.filterParamsObject(),
      })
        .then((res) => {
          this.dialogClearOffers = false;
          this.clearOffersLoading = false;
          const affected = res.data?.data?.affected ?? 0;
          this.$iziToast.success({
            timeout: 3000,
            message: this.$t("products.clearOffersSuccess", {
              count: affected,
            }),
            position: "bottomRight",
          });
          this.setRows();
        })
        .catch((err) => {
          this.clearOffersLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || this.$t("error"),
            position: "bottomRight",
          });
        });
    },
    mostOrder(item) {
      this.$axios({
        method: "POST",
        url: `is_most_order/${item.id}`,
      })
        .then(() => {
          this.setRows();
          this.$iziToast.error({
            timeout: 2000,
            message: this.$t("editSuccess"),
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 2000,
            message: err.response.data.message,
            position: "bottomRight",
          });
        });
    },
    // ==================== End CRUD ====================
    handelSelectedFile(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      this.selectedFile.file = file;
      this.selectedFile.name = file.name;
      this.selectedFile.path = URL.createObjectURL(file);
    },

    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },

    removeImportFile(inputId) {
      this.resetSelectedFile(inputId);
    },

    extractProductCodeFromFilename(filename) {
      if (!filename) return "";
      const base = filename.replace(/\.[^/.]+$/, "");
      return base.trim();
    },

    handleSelectedImages(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      this.revokeImagePreviews();

      this.selectedImageFiles = files.map((file) => ({
        file,
        name: file.name,
        code: this.extractProductCodeFromFilename(file.name),
        preview: URL.createObjectURL(file),
      }));
    },

    revokeImagePreviews() {
      this.selectedImageFiles.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
    },

    removeSelectedImage(index, inputId) {
      const item = this.selectedImageFiles[index];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      this.selectedImageFiles.splice(index, 1);

      if (!this.selectedImageFiles.length && inputId) {
        const input = document.getElementById(inputId);
        if (input) input.value = "";
      }
    },

    resetSelectedImages(inputId) {
      this.revokeImagePreviews();
      this.selectedImageFiles = [];

      if (inputId) {
        const input = document.getElementById(inputId);
        if (input) input.value = "";
      }
    },

    importImagesConfirm() {
      if (!this.selectedImageFiles.length) {
        this.$iziToast.error({
          timeout: 2000,
          message: this.$t("products.importImagesEmpty"),
          position: "bottomRight",
        });
        return;
      }

      this.importImagesLoading = true;
      const frmData = new FormData();
      this.selectedImageFiles.forEach((item) => {
        frmData.append("product_images[]", item.file);
      });

      this.$axios
        .post("products/import-images", frmData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          this.importModelImages = false;
          this.importImagesLoading = false;
          const data = res.data?.data || {};
          const parts = [];

          if (data.updated) {
            parts.push(
              this.$t("products.importImagesUpdated", { count: data.updated })
            );
          }
          if (data.created) {
            parts.push(
              this.$t("products.importImagesCreated", { count: data.created })
            );
          }
          if (data.not_found?.length) {
            parts.push(
              this.$t("products.importImagesNotFound", {
                codes: data.not_found.slice(0, 5).join(", "),
                extra:
                  data.not_found.length > 5
                    ? ` +${data.not_found.length - 5}`
                    : "",
              })
            );
          }

          this.$iziToast.success({
            timeout: 4000,
            message: parts.length ? parts.join(" · ") : res.data?.message,
            position: "bottomRight",
          });

          this.resetSelectedImages("file_input_images");
          this.setRows();
        })
        .catch((err) => {
          this.importImagesLoading = false;
          const data = err.response?.data?.data;
          let message = err.response?.data?.message || this.$t("products.exportFailed");

          if (data?.not_found?.length) {
            message += ` (${this.$t("products.importImagesNotFoundShort")}: ${data.not_found
              .slice(0, 5)
              .join(", ")})`;
          }

          this.$iziToast.error({
            timeout: 4000,
            message,
            position: "bottomRight",
          });
        });
    },

    resetSelectedFile(inputId) {
      if (this.selectedFile.path) {
        URL.revokeObjectURL(this.selectedFile.path);
      }

      this.selectedFile = {
        file: null,
        name: null,
        path: null,
      };

      if (inputId) {
        const input = document.getElementById(inputId);
        if (input) input.value = "";
      }
    },
    closeDeleteDialog() {
      this.dialogDelete = false;
      this.itemtoDelete = null;
    },
    closeDeleteSelectedDialog() {
      this.dialogDelete_selected = false;
    },
    closeClearOffersDialog() {
      if (this.clearOffersLoading) return;
      this.dialogClearOffers = false;
    },
    closeImportSheetDialog() {
      if (this.importLoading) return;
      this.importModel = false;
      this.resetSelectedFile("file_input_products");
    },
    closeImportCodePriceDialog() {
      if (this.importCodePriceLoading) return;
      this.importModelCodePrice = false;
      this.resetSelectedFile("file_input_code_price");
    },
    closeImportProductDescDialog() {
      if (this.importModelProductDescLoading) return;
      this.importModelProductDesc = false;
      this.resetSelectedFile("file_input_desc");
    },
    closeImportProductSeoDialog() {
      if (this.importModelProductSeoLoading) return;
      this.importModelProductSeo = false;
      this.resetSelectedFile("file_input_seo");
    },
    closeImportPriceOfferDialog() {
      if (this.importModelPriceOfferLoading) return;
      this.importModelPriceOffer = false;
      this.resetSelectedFile("file_input_offer");
    },
    closeImportStockDialog() {
      if (this.importStockLoading) return;
      this.importModelStock = false;
      this.stockImportErrors = [];
      this.resetSelectedFile("file_input_stock");
    },
    closeImportImagesDialog() {
      if (this.importImagesLoading) return;
      this.importModelImages = false;
      this.resetSelectedImages("file_input_images");
    },
    // Set Rows
    setRows() {
      this.loading = true;
      const params = this.buildFilterParams();
      params.append("page", this.paginations.current_page);
      params.append("per_page", this.paginations.items_per_page);

      this.$axios({
        method: "GET",
        url: "products",
        params: params,
      })
        .then((res) => {
          this.paginations.last_page = res.data.meta?.last_page;
          this.paginations.items_per_page = res.data.meta?.per_page;
          this.statisticsItem.number = res.data.meta.total;
          this.rows = res.data.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    generateAndDownloadXML() {
      this.downloadLink = null;
      this.loadingXml = true;

      this.$axios
        .get("all_products_without_pagiante", { params: this.filterParamsObject() })
        .then((res) => {
          this.uploadFinshed = true;
          this.xmlContent = this.generateXML(res.data.data);
        })
        .catch(() => {
          this.loadingXml = false;
        });
    },

    generateXML(products) {
      // Replace this with your XML generation logic
      const xmlProducts = products
        .map(
          (product) => `<item><g:id>${product.id}</g:id>
                    <title><![CDATA[${product.name}]]></title>
                    <description><![CDATA[${product.desc}]]></description>
                    <link>${this.$BaseWebUrl}/products/${product.id}</link>
                    <g:image_link>${product.image}</g:image_link>
                    <g:price>${product.price + " " + product.currency}</g:price>
                    <g:tax>
                        <g:country>SA</g:country>
                        <g:rate>${product.rate}</g:rate>
                        <g:tax_ship>${product.vat_percentage}</g:tax_ship>
                    </g:tax>
                    <g:shipping>
                        <g:country>SA</g:country>
                        <g:price>${
                          product.shipping + " " + product.currency
                        }</g:price>
                    </g:shipping>
                    <g:availability><![CDATA[${
                      product.availability
                    }]]></g:availability>
                    <g:google_product_category><![CDATA[${
                      product.category_name
                    }]]></g:google_product_category>
                    <g:identifier_exists>TRUE</g:identifier_exists>
                    <g:product_type><![CDATA[${product.type}]]></g:product_type>
                    <g:sale_price>${
                      product.price_after ? product.price_after : ""
                    }</g:sale_price>
                    <g:brand>${product.availability}</g:brand>
                    <g:color></g:color>
                    <g:additional_image_link><![CDATA[${
                      product.image
                    }]]></g:additional_image_link></item>`
        )
        .join("");
      const generatedXML = `<?xml version="1.0"?><rss version="2.0" xmlns:g="http://base.google.com/ns/1.0"><channel><created_at>${new Date().toLocaleString()}</created_at>
              ${xmlProducts}
           </channel></rss>`;
      const blob = new Blob([generatedXML], { type: "application/xml" });
      this.downloadLink = URL.createObjectURL(blob);
      this.loadingXml = false;
      this.downloadURI(this.downloadLink, "products.xml");
    },
    downloadURI(uri, name) {
      const link = document.createElement("a");
      link.download = name;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    fetchData(e) {
      this.paginations.current_page = e;
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            page: e,
          }),
        })
        .catch(() => {});
    },
  },

  // ======= hooks
  created() {
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setRows();
  },
  watch: {
    "$route.query": {
      deep: true,
      handler(query) {
        this.paginations.current_page = query.page ? +query.page : 1;
        this.setRows();
      },
    },
  },
};
</script>

<style lang="scss">
.products-page {
  --pp-radius: 14px;
  --pp-radius-sm: 10px;
  --pp-border: #e4e9ef;
  --pp-bg: #f5f7fa;
  --pp-card: #ffffff;
  --pp-text: #1a2332;
  --pp-muted: #6b7a8d;
  --pp-shadow: 0 4px 20px rgba(26, 35, 50, 0.06);

  .products-toolbar {
    padding: 0 16px;
  }

  .toolbar-card {
    background: var(--pp-card);
    border-radius: var(--pp-radius);
    border: 1px solid var(--pp-border);
    padding: 20px 24px;
    box-shadow: var(--pp-shadow);

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: var(--pp-text);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    &__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 28px;
      padding: 0 10px;
      border-radius: 20px;
      background: var(--mainColor-2);
      color: #fff;
      font-size: 14px;
      font-weight: 700;
    }
  }

  .page-subtitle {
    margin: 6px 0 0;
    font-size: 13px;
    color: var(--pp-muted);
    line-height: 1.5;
  }

  .bulk-delete-btn {
    border-color: #e57373 !important;
    color: #c62828 !important;
    background: #fff5f5 !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    border-radius: var(--pp-radius-sm) !important;
    height: 42px !important;

    &:hover {
      background: #ffebee !important;
    }
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--pp-border);

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  .stat-box {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: var(--pp-bg);
    border: 1px solid var(--pp-border);
    border-radius: var(--pp-radius-sm);
    transition: border-color 0.2s, box-shadow 0.2s;

    &--highlight {
      border-color: var(--mainColor-2);
      box-shadow: 0 0 0 3px rgba(39, 60, 152, 0.1);
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &--total {
        background: linear-gradient(135deg, var(--mainColor-2), #1e88e5);
      }

      &--active {
        background: linear-gradient(135deg, #43a047, #66bb6a);
      }

      &--inactive {
        background: linear-gradient(135deg, #e53935, #ef5350);
      }

      &--selected {
        background: linear-gradient(135deg, #7b1fa2, #ab47bc);
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;

      strong {
        font-size: 20px;
        font-weight: 800;
        color: var(--pp-text);
        line-height: 1.1;
      }

      span {
        font-size: 12px;
        color: var(--pp-muted);
        font-weight: 500;
      }
    }
  }

  .tools-panel {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--pp-border);

    &__head {
      display: flex;
      align-items: center;
      font-size: 13px;
      font-weight: 700;
      color: var(--pp-text);
      margin-bottom: 14px;
    }

    &__hint {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: -6px 0 14px;
      padding: 10px 12px;
      border-radius: 8px;
      background: rgba(39, 60, 152, 0.08);
      border: 1px solid rgba(39, 60, 152, 0.16);
      color: var(--mainColor-2);
      font-size: 12px;
      font-weight: 600;
      line-height: 1.45;
    }

    &__hint-icon {
      flex-shrink: 0;
      color: var(--mainColor-2);
    }
  }

  .tools-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .tools-group {
    padding: 14px;
    background: var(--pp-bg);
    border: 1px solid var(--pp-border);
    border-radius: var(--pp-radius-sm);

    &__label {
      display: block;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--pp-muted);
      margin-bottom: 10px;
    }

    &__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .tool-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: 1px solid var(--pp-border);
    border-radius: 8px;
    background: var(--pp-card);
    color: var(--pp-text);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    line-height: 1.3;

    .v-icon {
      color: var(--mainColor-2);
    }

    &:hover:not(:disabled) {
      border-color: var(--mainColor-2);
      background: rgba(39, 60, 152, 0.05);
      color: var(--mainColor-2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--link {
      text-decoration: none;
    }

    &--danger {
      border-color: #ffcdd2;
      color: #c62828;

      .v-icon {
        color: #c62828;
      }

      &:hover:not(:disabled) {
        border-color: #e53935;
        background: #ffebee;
        color: #b71c1c;
      }
    }
  }

  .products-table-wrap {
    padding: 16px;
  }

  .products-table {
    background: var(--pp-card) !important;
    border: 1px solid var(--pp-border);
    border-radius: var(--pp-radius);
    overflow: hidden;
    box-shadow: var(--pp-shadow);

    &.diff_table.thumb .v-data-table__wrapper tbody tr td {
      vertical-align: middle !important;
    }

    .product-thumb {
      width: 56px;
      height: 56px;
      margin: 0 auto;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid var(--pp-border);
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      &__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      &__placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--pp-bg);
        color: var(--pp-muted);
      }
    }

    .product-name {
      display: block;
      max-width: 220px;
      margin: 0 auto;
      font-weight: 600;
      font-size: 13px;
      color: var(--pp-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .product-code {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 6px;
      background: var(--pp-bg);
      border: 1px solid var(--pp-border);
      font-family: ui-monospace, monospace;
      font-size: 12px;
      font-weight: 600;
      color: var(--mainColor-2);
    }

    .text-muted {
      color: var(--pp-muted);
      font-size: 13px;
    }

    .desc-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: 1px solid var(--pp-border);
      border-radius: 8px;
      background: var(--pp-bg);
      cursor: pointer;
      transition: all 0.2s;

      .v-icon {
        color: var(--mainColor-2);
      }

      &:hover {
        border-color: var(--mainColor-2);
        background: rgba(39, 60, 152, 0.08);
      }
    }

    .most-order-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border: 1px dashed var(--mainColor-2);
      border-radius: 20px;
      background: rgba(39, 60, 152, 0.04);
      color: var(--mainColor-2);
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s;

      &:hover {
        background: rgba(39, 60, 152, 0.1);
      }
    }

    .most-order-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      border-radius: 20px;
      background: #e8f5e9;
      color: #2e7d32;
      font-size: 11px;
      font-weight: 700;
      white-space: nowrap;
    }

    .row-actions {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .row-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &--view {
        background: #e3f2fd;
        color: #1565c0;

        &:hover {
          background: #bbdefb;
        }
      }

      &--edit {
        background: #fff3e0;
        color: #ef6c00;

        &:hover {
          background: #ffe0b2;
        }
      }

      &--delete {
        background: #ffebee;
        color: #c62828;

        &:hover {
          background: #ffcdd2;
        }
      }
    }

    .table-empty {
      padding: 48px 16px;
      text-align: center;
      color: var(--pp-muted);

      p {
        margin: 12px 0 0;
        font-size: 14px;
      }
    }
  }

  .products-pagination {
    display: flex;
    justify-content: flex-end;
    padding: 8px 16px 24px;
  }

  .desc-modal {
    max-width: 640px;
    min-width: 320px;
    border: 2px solid var(--mainColor-2);
    border-radius: var(--pp-radius);
    padding: 20px 24px;

    h4 {
      font-size: 18px;
      text-align: center;
      color: var(--mainColor-2);
      margin: 0 0 16px;
      font-weight: 700;
    }

    &__body {
      max-height: 60vh;
      overflow-y: auto;
      font-size: 14px;
      line-height: 1.6;
      color: var(--pp-text);
    }
  }
}

.products-page .model_container .main_body .image {
  width: auto;
  max-width: 90vw;

  img {
    max-height: 70vh;
    object-fit: contain;
    display: block;
    max-width: 500px;
    border-radius: var(--pp-radius-sm, 10px);
  }
}

body.darkTheme .products-page {
  --pp-border: #3a3f4b;
  --pp-bg: #1e2128;
  --pp-card: #262a33;
  --pp-text: #e8eaed;
  --pp-muted: #9aa0a6;

  .toolbar-card,
  .products-table,
  .stat-box,
  .tools-group,
  .tool-btn {
    background: var(--pp-card);
  }

  .stat-box,
  .tools-group {
    background: var(--pp-bg);
  }

  .tool-btn {
    background: var(--pp-bg);
  }
}

/* Dialogs are teleported to body — styles must live on content-class, not .products-page */
.v-dialog__content .products-dialog,
.products-dialog {
  --pp-radius: 14px;
  --pp-radius-sm: 10px;
  --pp-border: #e4e9ef;
  --pp-bg: #f5f7fa;
  --pp-card: #ffffff;
  --pp-text: #1a2332;
  --pp-muted: #6b7a8d;
  --pp-shadow: 0 12px 40px rgba(26, 35, 50, 0.18);

  border-radius: var(--pp-radius) !important;
  box-shadow: var(--pp-shadow) !important;
  overflow: hidden;

  .v-card.pp-dialog {
    border-radius: var(--pp-radius) !important;
    overflow: hidden;
    background: var(--pp-card) !important;
    box-shadow: none !important;
  }

  .v-card__title {
    margin-bottom: 0 !important;
  }

  .v-card__text.pp-dialog__body {
    padding: 20px 24px !important;
    background: var(--pp-bg) !important;
    color: var(--pp-text) !important;
  }

  .v-card__actions {
    display: none !important;
  }

  .pp-dialog__hero {
    padding: 28px 24px 8px;
    text-align: center;
    background: var(--pp-card);
  }

  .pp-dialog__icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;

    &--warning {
      background: linear-gradient(135deg, #ff9800, #f57c00);
    }
  }

  .pp-dialog__title {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: var(--pp-text);
    line-height: 1.45;
  }

  .pp-dialog__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    background: linear-gradient(135deg, var(--mainColor-2), #1e88e5);
    color: #fff;
  }

  .pp-dialog__head-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    color: #fff;
  }

  .pp-dialog__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    cursor: pointer;
    transition: background 0.2s;
    flex-shrink: 0;

    .v-icon {
      color: #fff !important;
    }

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.28);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .pp-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid var(--pp-border);
    background: var(--pp-card);

    @media (max-width: 480px) {
      flex-direction: column-reverse;

      .dialog-btn {
        width: 100%;
      }
    }
  }

  .dialog-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 120px;
    height: 42px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, opacity 0.2s, transform 0.15s;
    border: none;
    outline: none;
    font-family: inherit;
    white-space: nowrap;

    .v-icon {
      flex-shrink: 0;
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &--cancel {
      background: var(--pp-bg);
      color: var(--pp-muted) !important;
      border: 1.5px solid var(--pp-border);

      .v-icon {
        color: var(--pp-muted) !important;
      }

      &:hover:not(:disabled) {
        border-color: var(--mainColor-2);
        color: var(--mainColor-2) !important;
        background: rgba(39, 60, 152, 0.05);

        .v-icon {
          color: var(--mainColor-2) !important;
        }
      }
    }

    &--confirm {
      background: var(--mainColor-2);
      color: #fff !important;
      box-shadow: 0 4px 14px rgba(39, 60, 152, 0.3);

      .v-icon {
        color: #fff !important;
      }

      &:hover:not(:disabled) {
        filter: brightness(1.05);
      }
    }

    &--danger {
      background: #e53935;
      color: #fff !important;
      box-shadow: 0 4px 14px rgba(229, 57, 53, 0.3);

      .v-icon {
        color: #fff !important;
      }

      &:hover:not(:disabled) {
        filter: brightness(1.05);
      }
    }

    &__spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: pp-dialog-spin 0.7s linear infinite;
      flex-shrink: 0;
    }
  }

  .import-dialog__hint {
    margin: 0 0 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(39, 60, 152, 0.08);
    border: 1px solid rgba(39, 60, 152, 0.12);
    color: var(--pp-text);
    font-size: 12px;
    line-height: 1.55;
  }

  .import-sample-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px dashed rgba(39, 60, 152, 0.35);
    border-radius: 10px;
    background: rgba(39, 60, 152, 0.06);
    color: var(--mainColor-2);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;

    .v-icon {
      color: var(--mainColor-2) !important;
    }

    &:hover:not(:disabled) {
      background: rgba(39, 60, 152, 0.1);
      border-color: var(--mainColor-2);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .import-upload {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__input {
      position: absolute;
      width: 0;
      height: 0;
      opacity: 0;
      pointer-events: none;
    }

    &__zone {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 168px;
      padding: 24px 20px;
      margin: 0;
      border: 2px dashed var(--pp-border);
      border-radius: 14px;
      background: var(--pp-card);
      cursor: pointer;
      text-align: center;
      transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

      &:hover {
        border-color: rgba(39, 60, 152, 0.45);
        background: rgba(39, 60, 152, 0.03);
      }
    }

    &__icon-wrap {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 52px;
      height: 52px;
      border-radius: 50%;
      background: rgba(39, 60, 152, 0.1);
      color: var(--mainColor-2);

      .v-icon {
        color: var(--mainColor-2) !important;
      }
    }

    &__title {
      max-width: 100%;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.45;
      color: var(--pp-text);
      word-break: break-word;
    }

    &__subtitle {
      font-size: 12px;
      line-height: 1.4;
      color: var(--pp-muted);
    }

    &__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      align-self: center;
      gap: 6px;
      padding: 6px 12px;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: #c62828;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;

      .v-icon {
        color: #c62828 !important;
      }

      &:hover {
        background: rgba(229, 57, 53, 0.08);
      }
    }

    &--filled {
      .import-upload__zone {
        border-style: solid;
        border-color: rgba(39, 60, 152, 0.28);
        background: rgba(39, 60, 152, 0.05);
        box-shadow: inset 0 0 0 1px rgba(39, 60, 152, 0.06);
      }

      .import-upload__icon-wrap {
        background: rgba(39, 60, 152, 0.16);
      }
    }
  }

  .import-images-list {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid var(--pp-border);
    border-radius: 12px;
    background: var(--pp-card);

    &__item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border-bottom: 1px solid var(--pp-border);

      &:last-child {
        border-bottom: none;
      }
    }

    &__preview {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border-radius: 8px;
      overflow: hidden;
      background: var(--pp-bg);
      border: 1px solid var(--pp-border);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    &__meta {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    &__code {
      font-size: 13px;
      font-weight: 700;
      color: var(--mainColor-2);
      line-height: 1.3;
    }

    &__name {
      font-size: 11px;
      color: var(--pp-muted);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__remove {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: #c62828;
      cursor: pointer;

      .v-icon {
        color: #c62828 !important;
      }

      &:hover {
        background: rgba(229, 57, 53, 0.08);
      }
    }
  }
}

@keyframes pp-dialog-spin {
  to {
    transform: rotate(360deg);
  }
}

body.darkTheme .products-dialog {
  --pp-border: #3a3f4b;
  --pp-bg: #1e2128;
  --pp-card: #262a33;
  --pp-text: #e8eaed;
  --pp-muted: #9aa0a6;
  --pp-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);

  .pp-dialog__hero,
  .pp-dialog__footer {
    background: var(--pp-card);
  }

  .pp-dialog__body,
  .v-card__text.pp-dialog__body {
    background: #1a1d24 !important;
  }

  .dialog-btn--cancel {
    background: #2a2f38;
  }

  .import-upload__zone {
    background: #2a2f38;
    border-color: #3a3f4b;

    &:hover {
      background: rgba(39, 60, 152, 0.12);
      border-color: rgba(39, 60, 152, 0.45);
    }
  }

  .import-upload--filled .import-upload__zone {
    background: rgba(39, 60, 152, 0.12);
    border-color: rgba(39, 60, 152, 0.35);
  }

  .import-dialog__hint {
    background: rgba(39, 60, 152, 0.12);
    border-color: rgba(39, 60, 152, 0.2);
    color: var(--pp-text);
  }

  .import-sample-btn {
    background: rgba(39, 60, 152, 0.12);
    border-color: rgba(39, 60, 152, 0.35);
    color: #c5cae9;

    .v-icon {
      color: #c5cae9 !important;
    }

    &:hover:not(:disabled) {
      background: rgba(39, 60, 152, 0.2);
    }
  }

  .import-images-list {
    background: #2a2f38;
    border-color: #3a3f4b;

    &__item {
      border-bottom-color: #3a3f4b;
    }

    &__preview {
      background: #1e2128;
      border-color: #3a3f4b;
    }
  }
}
</style>
